import { PrimaryButton } from "@/components/Buttons/PrimaryButton";
import { SecondaryButton } from "@/components/Buttons/SecondaryButton";
import { Card } from "@/components/Card/Card";
import { Spacing } from "@/components/Spacing/Spacing";
import { motion } from "framer-motion";
import { SHEET_INITIAL, SHEET_ANIMATE, SHEET_EXIT, SHEET_SPRING } from "@/components/Modal/sheetMotion";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { IconX } from "@tabler/icons-react";
import { FormInput } from "../Inputs/FormInput";
import {
  parseUrlField,
  formatUrlField,
  normalizeUrlInput,
  isValidUrlFieldValue,
} from "@/lib/utils/urlField";

interface PUrlEditorProps {
  /** Current stored value — bare URL or `[label](url)` markdown. */
  value: string;
  /** Cancel / backdrop / Escape — discards edits. */
  onCloseAction: () => void;
  /** Save — receives the new canonical value. Only called when valid. */
  onSaveAction: (newValue: string) => void;
}

/**
 * Modal editor for a `URL` additional-field value: a display-text input and
 * a URL input with Cancel / Save. Validation runs on Save — a scheme-less
 * URL is gracefully prefixed with `https://`, then the result must be a real
 * http(s) URL with a host + TLD (so `https://sadf` is rejected with an inline
 * error and the modal stays open).
 */
export const PUrlEditor: React.FC<PUrlEditorProps> = ({
  value,
  onCloseAction,
  onSaveAction,
}) => {
  const t = useTranslations("urlField");
  const [displayText, setDisplayText] = useState(
    () => parseUrlField(value).label,
  );
  const [url, setUrl] = useState(() => parseUrlField(value).url);
  // The URL error is only surfaced once the field has been left, so it does
  // not flash red on every keystroke while the user is still typing.
  const [urlBlurred, setUrlBlurred] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCloseAction();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCloseAction]);

  // Save-time graceful fix: a scheme-less URL gets `https://` prepended.
  const fixedUrl = normalizeUrlInput(url);
  const candidate = formatUrlField(fixedUrl, displayText);

  // Validity — drives both the inline error and the Save button.
  let urlError: string | undefined;
  if (!fixedUrl) {
    // No URL: a stray display text would be silently lost — flag it.
    if (displayText.trim()) urlError = t("urlRequired");
  } else if (!isValidUrlFieldValue(candidate)) {
    urlError = t("invalid");
  }

  const isDirty = candidate !== (value ?? "").trim();
  const canSave = !urlError && isDirty;

  const handleSave = () => {
    if (!canSave) return;
    onSaveAction(candidate);
  };

  const modalContent = (
    <motion.div
      className="fixed left-0 top-0 bg-black/50 flex items-end sm:items-center justify-center sm:pr-[var(--ai-rail-push)] transition-[padding] duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none h-dvh w-screen z-2000 cursor-default"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCloseAction();
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      <motion.div
        className="w-full sm:w-auto flex justify-center"
        initial={SHEET_INITIAL}
        animate={SHEET_ANIMATE}
        exit={SHEET_EXIT}
        transition={SHEET_SPRING}
      >
        <Card
          padding="p-6"
          width="w-full sm:w-[95vw] sm:max-w-125"
          rounded="rounded-t-3xl sm:rounded-3xl"
          shadow="shadow-xl"
          header={t("title")}
          icon={IconX}
          iconActionOnClick={onCloseAction}
        >
          <div className="flex flex-col items-center w-full">
            <Spacing height="h-6" />
            <div className="w-full flex flex-col gap-3">
              <FormInput
                id="url-editor-label"
                labelFontSize="body-2-regular"
                label={t("displayText")}
                placeholder={t("displayTextPlaceholder")}
                value={displayText}
                onChangeAction={setDisplayText}
                onEnterAction={handleSave}
                width="w-full"
              />
              <FormInput
                id="url-editor-url"
                type="url"
                labelFontSize="body-2-regular"
                label={t("url")}
                placeholder={t("urlPlaceholder")}
                value={url}
                onChangeAction={setUrl}
                onBlurAction={() => setUrlBlurred(true)}
                onEnterAction={handleSave}
                error={urlBlurred ? urlError : undefined}
                width="w-full"
              />
            </div>
            <Spacing height="h-8" />
            <div className="flex flex-row w-full space-x-3">
              <SecondaryButton
                text={t("cancel")}
                onClick={onCloseAction}
                width="w-1/2"
              />
              <PrimaryButton
                text={t("apply")}
                onClick={handleSave}
                width="w-1/2"
                disabled={!canSave}
              />
            </div>
          </div>
        </Card>
      </motion.div>
    </motion.div>
  );

  return typeof document !== "undefined"
    ? createPortal(modalContent, document.body)
    : null;
};
